import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Radio,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Switch,
  FormControlLabel,
  FormGroup,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
} from '@mui/material'
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useCustom, useNotification, useCustomMutation } from '@refinedev/core'
import { useAuth0 } from '@/utils/auth/auth0'
import { getTierLabel } from '../utils/tierUtils'

interface XeroCustomerWithInvoices {
  contact_id: string
  contact_name: string
  email: string | null
  contact_status: string
  created_at: string
  updated_at: string
  invoice_count: number
  total_invoiced: number
  total_paid: number
  total_outstanding: number
  last_invoice_date: string | null
}

interface OrganisationOption {
  org_uuid: string
  organisation_name: string
  payment_type: string
  billing_amount: number | null
  membership: string | null
  xero_contact_id: string | null
  sync_status: string
  assigned_user_count: number
}

interface InvoiceLineItem {
  description: string
  quantity: number
  unit_amount: number
  line_amount: number
  tax_amount?: number
}

interface InvoiceDetails {
  invoice_id: string
  invoice_number: string
  date_issued: string | null
  due_date: string | null
  status: string
  line_items: InvoiceLineItem[]
  sub_total: number
  total_tax: number
  total: number
  currency_code: string
}

interface OrganisationAccount {
  org_uuid: string
  name: string
  active: boolean
  status: string | null
  tier: number | null
  payment_type: string | null
  billing_frequency: string | null
  billing_amount: number | null
  membership: string | null
  membership_until?: string | null
}

interface ReverseReconciliationModalProps {
  open: boolean
  onClose: () => void
  xeroCustomer: XeroCustomerWithInvoices
  onComplete: () => void
}

export const ReverseReconciliationModal: React.FC<
  ReverseReconciliationModalProps
> = ({ open, onClose, xeroCustomer, onComplete }) => {
  const [organisations, setOrganisations] = useState<OrganisationOption[]>([])
  const [filteredOrganisations, setFilteredOrganisations] = useState<
    OrganisationOption[]
  >([])
  const [selectedOrganisation, setSelectedOrganisation] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrgUuid, setSelectedOrgUuid] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [organisationsLoading, setOrganisationsLoading] = useState(true)
  const [createHistoricalRecords, setCreateHistoricalRecords] = useState(true)
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(
    null
  )
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [selectedOrgAccount, setSelectedOrgAccount] =
    useState<OrganisationAccount | null>(null)
  const [accountLoading, setAccountLoading] = useState(false)
  const [editingBilling, setEditingBilling] = useState(false)
  const [billingFrequency, setBillingFrequency] = useState<string>('')
  const [billingAmount, setBillingAmount] = useState<number | null>(0)

  const { open: openNotification } = useNotification()
  const { getAccessTokenSilently } = useAuth0()

  // Fetch all organisations that can be linked
  const { refetch: fetchOrganisations } = useCustom({
    url: '/xero/reconciliation/all-organisations',
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setOrganisations(data.data)
        setFilteredOrganisations(data.data)
        setOrganisationsLoading(false)
      },
      onError: () => {
        openNotification?.({
          message: 'Failed to load organisations',
          type: 'error',
        })
        setOrganisationsLoading(false)
      },
    },
  })

  // Execute assignment
  const { mutate: executeAssignment } = useCustomMutation()

  // Fetch invoice details
  const { refetch: fetchInvoiceDetails } = useCustom({
    url: `/xero/customer/${xeroCustomer.contact_id}/latest-invoice`,
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setInvoiceDetails(data.data)
        setInvoiceLoading(false)
      },
      onError: (error: any) => {
        setInvoiceLoading(false)
      },
    },
  })

  const fetchOrgAccount = async (orgUuid: string) => {
    if (!orgUuid || orgUuid === 'undefined' || orgUuid === 'null') {
      setAccountLoading(false)
      setSelectedOrgAccount(null)
      return
    }

    setAccountLoading(true)

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/orgs/account/${orgUuid}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await getAccessTokenSilently()}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // Find the organisation name from the organisations list
      const selectedOrg = organisations.find(
        (org) => org.org_uuid === orgUuid
      )

      // Use the actual API response structure
      const accountData = {
        ...data,
        name: selectedOrg?.organisation_name || 'Unknown Organisation',
      }

      setSelectedOrgAccount(accountData)
      setBillingFrequency(data.billing_frequency || 'Monthly')
      setBillingAmount(
        data.billing_amount !== null ? parseFloat(data.billing_amount) : null
      )
      setAccountLoading(false)
    } catch (error: any) {
      setAccountLoading(false)
      setSelectedOrgAccount(null)
      openNotification?.({
        message: `Failed to load organisation account details: ${error?.message || 'Unknown error'}`,
        type: 'error',
      })
    }
  }

  // Update billing
  const { mutate: updateBilling } = useCustomMutation()

  useEffect(() => {
    if (open) {
      fetchOrganisations()
      setSelectedOrganisation('')
      setSelectedOrgUuid('')
      setSearchTerm('')
      setCreateHistoricalRecords(true)
      setInvoiceDetails(null)
      setSelectedOrgAccount(null)
      setEditingBilling(false)

      // Fetch invoice details
      setInvoiceLoading(true)
      fetchInvoiceDetails()
    }
  }, [open, fetchOrganisations, fetchInvoiceDetails])

  // Fetch organisation account details when an organisation is selected
  useEffect(() => {
    if (selectedOrganisation) {
      setSelectedOrgAccount(null)
      fetchOrgAccount(selectedOrganisation)
    }
  }, [selectedOrganisation])

  useEffect(() => {
    let filtered = organisations

    // Filter out organisations already linked to Xero customers
    filtered = filtered.filter((org) => !org.xero_contact_id)

    // Apply search filter (case-insensitive)
    if (searchTerm) {
      filtered = filtered.filter((org) =>
        org.organisation_name.toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    }

    setFilteredOrganisations(filtered)
  }, [searchTerm, organisations])

  const isLikelyMatch = (orgName: string, customerName: string): boolean => {
    const cleanOrg = orgName.toLowerCase().trim()
    const cleanCustomer = customerName.toLowerCase().trim()

    // Check if organisation name contains customer name or vice versa
    return cleanOrg.includes(cleanCustomer) || cleanCustomer.includes(cleanOrg)
  }

  // Sort organisations: potential matches first, then alphabetically
  const sortedOrganisations = filteredOrganisations.sort((a, b) => {
    const aMatch = isLikelyMatch(a.organisation_name, xeroCustomer.contact_name)
    const bMatch = isLikelyMatch(b.organisation_name, xeroCustomer.contact_name)

    // If one is a match and the other isn't, prioritize the match
    if (aMatch && !bMatch) return -1
    if (!aMatch && bMatch) return 1

    // If both are matches or both aren't matches, sort alphabetically
    return a.organisation_name.localeCompare(b.organisation_name)
  })

  const handleUpdateBilling = async () => {
    if (!selectedOrganisation || !selectedOrgAccount) return

    // Include ALL required fields for the API
    const updateData: any = {
      org_uuid: selectedOrganisation,
      membership: selectedOrgAccount.membership,
      membership_until: selectedOrgAccount.membership_until,
      tier: selectedOrgAccount.tier,
      payment_type: selectedOrgAccount.payment_type,
      billing_frequency: billingFrequency,
      billing_amount: billingAmount,
    }

    updateBilling(
      {
        url: `/orgs/account/${selectedOrganisation}`,
        method: 'patch',
        values: updateData,
      },
      {
        onSuccess: async () => {
          openNotification?.({
            message: 'Billing details updated successfully',
            type: 'success',
          })
          setEditingBilling(false)
          // Refresh the account data
          if (selectedOrganisation) {
            await fetchOrgAccount(selectedOrganisation)
          }
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to update billing details',
            type: 'error',
          })
        },
      }
    )
  }

  const handleExecuteAssignment = () => {
    if (!selectedOrganisation) return

    setLoading(true)
    executeAssignment(
      {
        url: '/xero/reconciliation/assign',
        method: 'post',
        values: {
          org_uuid: selectedOrganisation,
          xero_contact_id: xeroCustomer.contact_id,
          create_historical_records: true, // Always create historical records
        },
      },
      {
        onSuccess: (data: any) => {
          openNotification?.({
            message: `Successfully linked ${xeroCustomer.contact_name} to organisation`,
            type: 'success',
          })
          setLoading(false)
          onComplete()
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to link Xero customer to organisation',
            type: 'error',
          })
          setLoading(false)
        },
      }
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(amount)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Link Xero Customer to Organisation</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Organisation for {xeroCustomer.contact_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose which organisation this Xero customer should be linked to.
            Historical billing records will be created automatically.
          </Typography>
        </Box>

        {/* Xero Customer Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ReceiptIcon color="primary" />
                  <Typography variant="subtitle1">Xero Customer</Typography>
                </Stack>
                <Typography variant="h6">
                  {xeroCustomer.contact_name}
                </Typography>
                {xeroCustomer.email && (
                  <Typography variant="body2" color="text.secondary">
                    {xeroCustomer.email}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Financial Summary
                </Typography>
                <Typography variant="body2">
                  {xeroCustomer.invoice_count} invoices •{' '}
                  {formatCurrency(xeroCustomer.total_invoiced)} total
                </Typography>
                {xeroCustomer.total_outstanding > 0 && (
                  <Typography variant="body2" color="error.main">
                    {formatCurrency(xeroCustomer.total_outstanding)} outstanding
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Selected Organisation Details - Show above the list */}
        {selectedOrganisation && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <BusinessIcon color="primary" />
                <Typography variant="h6">Selected Organisation</Typography>
                {selectedOrgAccount && !editingBilling && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => setEditingBilling(true)}
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    Edit Billing
                  </Button>
                )}
              </Stack>

              {accountLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : selectedOrgAccount ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Organisation Info
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {selectedOrgAccount.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tier:</strong>{' '}
                      {getTierLabel(selectedOrgAccount.tier, 'Not set')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Membership:</strong>{' '}
                      {selectedOrgAccount.membership || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Billing Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Payment Type:</strong>{' '}
                      {selectedOrgAccount.payment_type || 'Not set'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Amount:</strong>{' '}
                      {selectedOrgAccount.billing_amount !== null
                        ? formatCurrency(selectedOrgAccount.billing_amount)
                        : 'Not set'}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2">
                        <strong>Frequency:</strong>
                      </Typography>
                      {editingBilling ? (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={billingFrequency}
                            onChange={(e) =>
                              setBillingFrequency(e.target.value)
                            }
                          >
                            <MenuItem value="Monthly">Monthly</MenuItem>
                            <MenuItem value="Quarterly">Quarterly</MenuItem>
                            <MenuItem value="Yearly">Yearly</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">
                          {selectedOrgAccount.billing_frequency || 'Not set'}
                        </Typography>
                      )}
                    </Stack>
                    {editingBilling && (
                      <Box sx={{ mt: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">
                            <strong>Amount:</strong>
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={billingAmount === null ? '' : billingAmount}
                            onChange={(e) => {
                              const value = e.target.value
                              if (value === '') {
                                setBillingAmount(null)
                              } else {
                                const numValue = Number(value)
                                setBillingAmount(numValue)
                              }
                            }}
                            sx={{ width: 120 }}
                            inputProps={{
                              step: 0.01,
                            }}
                            placeholder="Enter amount or leave empty"
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Leave blank to use default tier pricing
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  {editingBilling && (
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                          startIcon={<SaveIcon />}
                          onClick={handleUpdateBilling}
                          size="small"
                          variant="contained"
                        >
                          Save
                        </Button>
                        <Button
                          startIcon={<CancelIcon />}
                          onClick={() => {
                            setEditingBilling(false)
                            setBillingFrequency(
                              selectedOrgAccount.billing_frequency || 'Monthly'
                            )
                            setBillingAmount(
                              selectedOrgAccount.billing_amount !== null
                                ? parseFloat(
                                    selectedOrgAccount.billing_amount.toString()
                                  )
                                : null
                            )
                          }}
                          size="small"
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="info">Loading organisation details...</Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Latest Invoice Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <ReceiptIcon color="primary" />
              <Typography variant="h6">Latest Invoice Details</Typography>
            </Stack>
            {invoiceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : invoiceDetails ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Invoice #:</strong> {invoiceDetails.invoice_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong>{' '}
                    {invoiceDetails.date_issued
                      ? new Date(
                          invoiceDetails.date_issued
                        ).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{' '}
                    <Chip
                      size="small"
                      label={invoiceDetails.status}
                      color="primary"
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong>{' '}
                    {formatCurrency(invoiceDetails.total)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Line Items
                  </Typography>
                  {invoiceDetails.line_items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.quantity} × {formatCurrency(item.unit_amount)} ={' '}
                        {formatCurrency(item.line_amount)}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">
                No invoice details available for this customer
              </Alert>
            )}
          </CardContent>
        </Card>

        <TextField
          fullWidth
          size="small"
          placeholder="Search organisations (case-insensitive)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ mb: 2 }}
        />

        {organisationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 3 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Organisation Name</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell>Billing Amount</TableCell>
                  <TableCell>Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedOrganisations.map((org) => {
                  const isPotentialMatch = isLikelyMatch(
                    org.organisation_name,
                    xeroCustomer.contact_name
                  )
                  return (
                    <TableRow
                      key={org.org_uuid}
                      hover
                      selected={selectedOrganisation === org.org_uuid}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isPotentialMatch
                          ? 'action.hover'
                          : 'inherit',
                      }}
                      onClick={() => setSelectedOrganisation(org.org_uuid)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight="medium">
                            {org.organisation_name}
                          </Typography>
                          {isPotentialMatch && (
                            <Chip
                              size="small"
                              label="Potential Match"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={org.payment_type} />
                      </TableCell>
                      <TableCell>
                        {org.billing_amount
                          ? formatCurrency(org.billing_amount)
                          : 'Not set'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={org.assigned_user_count}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExecuteAssignment}
          disabled={!selectedOrganisation || loading}
          color="primary"
        >
          {loading ? <CircularProgress size={20} /> : 'Link Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
